import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import {
    QuoteButton,
    RentButton,
    ReserveButton,
    SelectButton,
    CallForAvailability,
} from "@/components/units/actions";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { QuoteType, WidgetLayout } from "@/types/enums";
import { useRentalAction } from "@/hooks/useRentalAction";
import { useFacility } from "@/hooks/useFacility";
import { useTriggerButtonVisibility } from "@/hooks/useTriggerButtonVisibility";
import { useReservationAction } from "@/hooks/useReservationAction";
import { useUnitItemContext } from "@/hooks/useUnitItemContext";
import { emitUnitEvent } from "@/utils/units/unitEvents";
import type { UnitGroup } from "@/types";

interface TriggerButtonsProps {
    unitGroup: UnitGroup;
    selectedPromo: number;
}

export const TriggerButtons = ({
    unitGroup,
    selectedPromo,
}: TriggerButtonsProps) => {
    const displayOptions = useDisplayOptionContext();
    const { openVbp, openRentalFunnel, label: rentLabel } = useRentalAction(
        unitGroup,
        selectedPromo,
    );
    const { disableSoftReservation, widgetLayout } = displayOptions;
    const { facility, isResolved: isFacilityResolved } = useFacility(
        unitGroup.representative_unit.facility_id,
    );
    const {
        hasMultipleSubGroups,
        hasAvailableUnits,
        canReserve,
        canRent,
        shouldShowSelectButton,
        isClickToCall,
    } = useTriggerButtonVisibility(unitGroup);

    const { openReservationFunnel, label: reserveLabel } = useReservationAction(
        unitGroup,
        selectedPromo,
    );

    const { promoExpandedBy, setPromoExpandedBy, promoExpandedPanelId } =
        useUnitItemContext();

    const phone = facility?.phone || "";
    const quoteType = facility?.settings?.quote_type;

    const isLayout1 = widgetLayout === WidgetLayout.LAYOUT_1;
    const isLayout2 = widgetLayout === WidgetLayout.LAYOUT_2;
    // Both layouts now expand the row in place (Layout 1: WEB-1432, Layout 2: WEB-1436).
    // Named for intent and as the shared seam WEB-1502's featured work builds on.
    const inlineExpansionEnabled = isLayout1 || isLayout2;
    const hasMultiplePromos = unitGroup.rates.promo_rates.length > 1;
    // Layout-2-only: swaps Rent/Reserve for a single Close button on multi-promo.
    // Layout 1 keeps all triggers visible and shows the active one's ✕ via CSS.
    const shouldToggleExpand = isLayout2 && hasMultiplePromos;
    // VBP (multi-subgroup) Reserve opens the unit-selection modal — unless the facility
    // hides Reserve in that modal, in which case it falls back to the in-row form. Either
    // way it never navigates straight to the reserve page. Single-subgroup units always
    // use the in-row form.
    const hideReserveOnVbpModal =
        facility?.settings?.hide_reservation_button_on_vbp_modal ?? false;
    const reserveOpensVbp = hasMultipleSubGroups && !hideReserveOnVbpModal;
    const reserveTogglesInline = inlineExpansionEnabled && !reserveOpensVbp;
    // Quote keeps its own toggle button, so only rent/reserve swap to Close.
    const isRentReserveExpanded =
        promoExpandedBy === "rent" || promoExpandedBy === "reserve";
    const closeLabel =
        displayOptions.language?.action?.closeButton ?? "Close";

    // quote_type-dependent slot (soft reservation already known to be enabled).
    // Until the facility query resolves, quote_type is unknown — show a neutral
    // placeholder so "Call for Availability" doesn't flash before Get a Quote.
    const renderQuoteOrCall = () =>
        !isFacilityResolved ? (
            <Skeleton className="tw:h-9 tw:w-full tw:rounded-full" />
        ) : quoteType === QuoteType.RESERVE ? (
            <QuoteButton unitGroup={unitGroup} selectedPromo={selectedPromo} />
        ) : (
            <CallForAvailability phone={phone} />
        );

    const handleRentalClick = () => {
        if (shouldToggleExpand) {
            setPromoExpandedBy(promoExpandedBy ? null : "rent");
            return;
        }
        if (hasMultipleSubGroups) {
            return openVbp();
        } else {
            return openRentalFunnel();
        }
    };

    const handleReserveClick = () => {
        // VBP units open the unit-selection modal (Reserve from inside it navigates) — mirrors Rent.
        if (reserveOpensVbp) {
            return openVbp();
        }
        if (reserveTogglesInline) {
            const isOpening = !promoExpandedBy;
            if (isOpening) {
                // Fire the reservation intent event on open (inline expansion no longer navigates) — timing pending GTM.
                emitUnitEvent(
                    unitGroup.representative_unit.id,
                    unitGroup.representative_unit.facility_id,
                    selectedPromo,
                    "reservation",
                );
            }
            setPromoExpandedBy(isOpening ? "reserve" : null);
            return;
        }
        return openReservationFunnel();
    };

    /*
     * Click-to-call units only show a Call for Availability button.
     */
    if (isClickToCall) {
        return <CallForAvailability phone={phone} />;
    }

    /*
     * If the unit group has available units, we will either show the
     * Select button, or the individual action buttons.
     */
    if (hasAvailableUnits) {
        if (shouldShowSelectButton()) {
            // Layout 2 multi-promo: Select button toggles expand row
            if (shouldToggleExpand) {
                if (isRentReserveExpanded) {
                    return (
                        <div>
                            {/* Hidden spacer preserves top-slot height (aligns Close with Reserve position) */}
                            <div style={{ visibility: "hidden" }} aria-hidden="true">
                                <Button variant="default" className="rent-btn" tabIndex={-1} type="button">
                                    {closeLabel}
                                </Button>
                            </div>
                            <Button
                                variant="default"
                                data-testid="closeButton"
                                onClick={() => setPromoExpandedBy(null)}
                                className="close-btn"
                                aria-label={closeLabel}
                                aria-expanded={true}
                                aria-controls={promoExpandedPanelId}
                                type="button"
                            >
                                {closeLabel}
                            </Button>
                        </div>
                    );
                }
                const selectLabel =
                    displayOptions.language?.action?.selectButton ?? "Select";
                return (
                    <div>
                        {/* Hidden spacer aligns Select with Reserve position on grid cards */}
                        <div style={{ visibility: "hidden" }} aria-hidden="true">
                            <Button variant="default" className="rent-btn" tabIndex={-1} type="button">
                                {selectLabel}
                            </Button>
                        </div>
                        <SelectButton
                            unitGroup={unitGroup}
                            selectedPromo={selectedPromo}
                            onClick={handleRentalClick}
                            label={selectLabel}
                            ariaControls={promoExpandedPanelId}
                        />
                    </div>
                );
            }
            return (
                <SelectButton
                    unitGroup={unitGroup}
                    selectedPromo={selectedPromo}
                />
            );
        }
        if (shouldToggleExpand && isRentReserveExpanded) {
            return (
                <div>
                    {/* Hidden Rent button preserves top-slot height */}
                    {canRent && (
                        <div style={{ visibility: "hidden" }} aria-hidden="true">
                            <RentButton
                                handleRentalClick={() => {}}
                                label={rentLabel}
                            />
                        </div>
                    )}
                    {/* Close button in the Reserve (bottom) position */}
                    <Button
                        variant="default"
                        data-testid="closeButton"
                        onClick={() => setPromoExpandedBy(null)}
                        className="close-btn tw:mt-1.5"
                        aria-label={closeLabel}
                        aria-expanded={true}
                        aria-controls={promoExpandedPanelId}
                        type="button"
                    >
                        {closeLabel}
                    </Button>
                </div>
            );
        }

        return (
            <div>
                {canRent && (
                    <RentButton
                        handleRentalClick={handleRentalClick}
                        label={rentLabel}
                        ariaControls={
                            shouldToggleExpand
                                ? promoExpandedPanelId
                                : undefined
                        }
                    />
                )}
                {canReserve && (
                    <ReserveButton
                        onReservationClick={handleReserveClick}
                        label={reserveLabel}
                        ariaControls={
                            reserveTogglesInline
                                ? promoExpandedPanelId
                                : undefined
                        }
                        ariaExpanded={
                            reserveTogglesInline
                                ? promoExpandedBy === "reserve"
                                : undefined
                        }
                    />
                )}
                {/* Safety fallback. */}
                {!canRent && !canReserve && (
                    <>
                        {disableSoftReservation ? (
                            <CallForAvailability phone={phone} />
                        ) : (
                            renderQuoteOrCall()
                        )}
                    </>
                )}
            </div>
        );
    }

    /*
     * No available units. We will either show the Quote button,
     * or Call for Availability.
     *
     * At this point, 'reservation_type' is NOT 'click_to_call', and
     * there are no available units.
     */

    // Sold-out multi-promo: show Close while the quote selector is open (no spacer — single button).
    if (shouldToggleExpand && promoExpandedBy === "quote") {
        return (
            <Button
                variant="default"
                data-testid="closeButton"
                onClick={() => setPromoExpandedBy(null)}
                className="close-btn"
                aria-label={closeLabel}
                aria-expanded={true}
                aria-controls={promoExpandedPanelId}
                type="button"
            >
                {closeLabel}
            </Button>
        );
    }

    if (disableSoftReservation) {
        return <CallForAvailability phone={phone} />;
    } else {
        return renderQuoteOrCall();
    }
};

TriggerButtons.displayName = "TriggerButtons";
